import React, { createContext, useCallback, useRef, useState } from "react";

import type { Consumer as Cable } from "@rails/actioncable";

const ActionCableContext = createContext<{
  cable?: Cable;
  setCable: (_cable: Cable | undefined) => void;
  channels: React.MutableRefObject<any>;
  handlers: React.MutableRefObject<{
    [name: string]: {
      onReceived: { [key: string]: any };
    };
  }>;
}>({
  setCable: () => {},
  channels: { current: {} },
  handlers: { current: {} },
});

export default ActionCableContext;

type Props = {
  children: React.ReactNode;
};

export const Consumer = ActionCableContext.Consumer;

export function Provider(props: Props) {
  const [cable, setCableState] = useState<Cable | undefined>();

  const channelsRef = useRef<Record<string, any>>({});
  const handlersRef = useRef<{
    [name: string]: { onReceived: { [key: string]: any } };
  }>({});

  /**
   * Replace the cable instance.
   * We must disconnect the previous cable and clear the channels map so that
   * useChannel() re-subscribes fresh subscriptions on the new consumer.
   * Handler registrations are intentionally preserved so components don't need
   * to re-register their onReceived callbacks.
   */
  const setCable = useCallback((newCable: Cable | undefined) => {
    setCableState((prev) => {
      if (prev) {
        try {
          prev.disconnect();
        } catch (_) {
          // ignore errors during disconnect
        }
      }
      // Clear stale subscriptions so useChannel recreates them on the new cable.
      channelsRef.current = {};
      return newCable;
    });
  }, []);

  const value = {
    cable,
    setCable,
    channels: channelsRef,
    handlers: handlersRef,
  };

  return (
    <ActionCableContext.Provider value={value}>
      {props.children}
    </ActionCableContext.Provider>
  );
}
